import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Phone, Mail, ArrowRightLeft, Clock, Pencil, Trash2,
  BellPlus, CalendarClock, Check, Loader2, Tag, SlidersHorizontal, Ban, User,
  Layers, KanbanSquare,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getProspect,
  addProspectNote,
  setProspectNextCall,
  setProspectReminder,
  getProspectMatches,
  getProspectAppraisalPrefill,
  getProspectContact,
  setProspectUnsubscribed,
  deleteProspect,
} from "../../../api/database";
import BuyerPreferencesModal from "../../contacts/components/BuyerPreferencesModal";
import { MarketStatusLine } from "./MarketStatus";
import AgentNotesSummary from "../../../components/notes/AgentNotesSummary";
import {
  useLinkedNotes,
  LinkedNotesSection,
  linkedToSummaryNotes,
} from "../../../components/notes/linkedNotes";

const money = (v) =>
  v ? "$" + Number(v).toLocaleString() : "—";

// A plain date field (2013-01-25) as 25/01/2013, formatted off the string so no
// timezone shift can move it a day.
const auDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

// The last sale as the sheet recorded it: a price, a date, or both.
const soldSummary = (op = {}) =>
  [op.last_sold_price ? "$" + Number(op.last_sold_price).toLocaleString() : "",
   auDate(op.last_sold_date)]
    .filter(Boolean)
    .join(" · ");

const TEMP_STYLES = {
  cold: "bg-[#eaf4fb] text-[#2f9bd6]",
  warm: "bg-[#fdeede] text-[#e8923a]",
  hot: "bg-[#e9f7ee] text-[#34b56a]",
};

const TempChip = ({ temp }) =>
  temp ? (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-extrabold ${TEMP_STYLES[temp] || ""}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {temp}
    </span>
  ) : null;

// Call-back series, matching the "Remind me to call again" picker used on contact
// notes so the Database behaves the same everywhere (Christian Gravias, 2026-08-03).
const PRESETS = [
  { key: "1d", label: "1 day" },
  { key: "1w", label: "1 week" },
  { key: "1m", label: "1 month" },
  { key: "3m", label: "3 months" },
  { key: "6m", label: "6 months" },
  { key: "12m", label: "12 months" },
];

// Why the agent is calling back. Carries into Who-to-Call and Tasks.
const REASONS = [
  { key: "contact", label: "Contact" },
  { key: "follow_up", label: "Follow Up" },
  { key: "appointment", label: "Appointment" },
  { key: "action_required", label: "Action Required" },
  { key: "review", label: "Review" },
];

// `readOnly` is set when the row belongs to a Database shared view-only:
// everything is visible, nothing can be changed (the API refuses it too).
export default function ProspectDrawer({ recordId, view, onClose, onChanged, onEdit, onMoveToPipeline, readOnly = false }) {
  const isOwner = view === "owners";
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [matches, setMatches] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  // Buying preferences: { contactId, title } once the row's CRM contact is
  // resolved, plus which person's button is mid-resolve.
  const [prefs, setPrefs] = useState(null);
  const [prefsFor, setPrefsFor] = useState(null);
  // Which person's Do-not-contact button is mid-save (0 = primary, 1 = second).
  const [unsubFor, setUnsubFor] = useState(null);
  // Which person's Profile button is mid-resolve.
  const [profileFor, setProfileFor] = useState(null);

  useEffect(() => {
    if (!recordId) return;
    let active = true;
    getProspect(recordId).then((res) => {
      if (active) setRecord(res.data);
    });
    if (isOwner) {
      getProspectMatches(recordId)
        .then((res) => active && setMatches(res.data.matches || []))
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [recordId, isOwner]);

  // Notes about this owner typed elsewhere - the appraisal, the Seller Lead,
  // the contact - so the row shows the whole history (CRM-2026-00106).
  const linked = useLinkedNotes(
    recordId && isOwner ? { record: recordId, from: "database" } : {},
    (record?.notes || []).length
  );

  if (!recordId) return null;

  const op = record?.owner_profile || {};
  const bp = record?.buyer_profile || {};

  // A jointly-owned property is ONE property with TWO owners, so the row splits
  // into two contacts that each keep their own name, mobile and email
  // (Christian Gravias, 2026-08-03). Anything past the two people (a third
  // number on the row) stays with the primary contact.
  const people = record
    ? [
        {
          name: record.name,
          mobiles: [record.mobile, ...(record.additional_mobiles || [])].filter(Boolean),
          emails: [record.email, ...(record.additional_emails || [])].filter(Boolean),
          // Do not contact, per person: the flag lives on this person's mirrored
          // CRM contact, not on the row (Paul Jimoulis, Monday 12931774512).
          unsubscribed: Boolean(record.people?.[0]?.unsubscribed),
        },
        ...(record.second_name || record.second_mobile || record.second_email
          ? [
              {
                name: record.second_name,
                mobiles: [record.second_mobile].filter(Boolean),
                emails: [record.second_email].filter(Boolean),
                unsubscribed: Boolean(record.people?.[1]?.unsubscribed),
              },
            ]
          : []),
      ]
    : [];
  const isJoint = people.length > 1;
  const bothNames = people.map((p) => p.name).filter(Boolean).join(" & ");
  const unsubbedNames = people.filter((p) => p.unsubscribed).map((p) => p.name || "Unnamed");
  // The header's own Call / Email buttons act on the PRIMARY person.
  const primaryBlocked = Boolean(people[0]?.unsubscribed);

  const handleNote = async () => {
    const body = noteText.trim();
    if (!body) return;
    setSaving(true);
    try {
      await addProspectNote(recordId, body);
      setNoteText("");
      const res = await getProspect(recordId);
      setRecord(res.data);
      toast.success("Note saved");
      onChanged && onChanged();
    } finally {
      setSaving(false);
    }
  };

  // Saves the callback (record + Tasks) and mirrors a reminder carrying the
  // chosen reason and time of day into Who-to-Call.
  const handleNextCall = async (payload) => {
    const res = await setProspectNextCall(recordId, payload);
    setRecord(res.data);
    onChanged && onChanged();
    return res.data;
  };

  const handleReminder = async () => {
    await setProspectReminder(recordId, {});
    toast.success("Reminder added to your Tasks");
  };

  // "Convert to appraisal" opens the Add Appraisal page with the property address
  // (and owner details) already filled in. Once that appraisal is saved the row
  // stays in the database, flagged as Appraisal (Monday 13198140343).
  const handleConvert = async () => {
    setConverting(true);
    try {
      const res = await getProspectAppraisalPrefill(recordId);
      const prefill = res.data?.prefill || {};
      navigate("/properties/add-property", {
        state: {
          listingType: "appraisal",
          prospectRecordId: recordId,
          prefill,
        },
      });
      onClose();
    } catch (e) {
      toast.error("Couldn't open the appraisal form. Please try again.");
    } finally {
      setConverting(false);
    }
  };

  // "Buying preferences" for one person on the row. An agent working an owner
  // list keeps finding people who want to BUY, so they need to capture what
  // they're after while they're on the phone (Christian Gravias, 2026-08-05).
  //
  // Resolved through the record rather than the person's email: Database rows
  // are routinely email-less, and the editor is keyed on a CRM contact id. The
  // server returns the row's mirrored contact, creating it if it's missing.
  const openPrefs = async (index = 0) => {
    setPrefsFor(index);
    try {
      const res = await getProspectContact(recordId, index === 0 ? "primary" : "secondary");
      const contactId = res.data?.contact_id;
      if (!contactId) throw new Error("no contact");
      setPrefs({
        contactId,
        title: people[index]?.name || res.data?.contact_name || "",
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Couldn't open buying preferences for this contact — try again"
      );
    } finally {
      setPrefsFor(null);
    }
  };

  // Open one person's CRM contact profile — the "digital profile" every owner
  // is meant to have, holding their notes, comms history and every OTHER
  // property they own (George Bottas, CRM-2026-00031).
  //
  // Resolved the same way as Prefs: through the record, not the email. Most
  // Database rows have no email, and the server mirrors the contact in on the
  // spot if the row never got one.
  const openProfile = async (index = 0) => {
    setProfileFor(index);
    try {
      const res = await getProspectContact(recordId, index === 0 ? "primary" : "secondary");
      const contactId = res.data?.contact_id;
      if (!contactId) throw new Error("no contact");
      navigate(`/contacts/${contactId}`);
      onClose();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Couldn't open a profile for this contact — add a name, mobile or email to the row first"
      );
    } finally {
      setProfileFor(null);
    }
  };

  // Flag ONE person on the row (or lift it). The write goes to their mirrored CRM
  // contact, which is what excludes them from every campaign audience and what
  // blacklists their email/mobile so next month's CSV can't resurrect them.
  const toggleUnsubscribed = async (index) => {
    const person = people[index];
    const on = !person?.unsubscribed;
    const who = person?.name || (index === 0 ? "this contact" : "the second contact");
    if (!on && !window.confirm(`Allow contact with ${who} again?`)) return;

    setUnsubFor(index);
    try {
      const res = await setProspectUnsubscribed(
        recordId, index === 1 ? "secondary" : "primary", on);
      // The endpoint hands back the refreshed row, so the drawer repaints without
      // a second fetch. `kept` means they opted out themselves and it stayed.
      if (res.data?.record) setRecord(res.data.record);
      if (res.data?.kept?.length) toast(res.data.detail, { duration: 8000 });
      else toast.success(res.data?.detail || "Saved");
      onChanged && onChanged();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't save that — try again");
    } finally {
      setUnsubFor(null);
    }
  };

  const handleDelete = async () => {
    const label = (isOwner ? op.address : record?.name) || record?.name || "this contact";
    if (!window.confirm(`Delete ${label} permanently? This removes the contact, its notes and history — it can't be undone.`)) return;
    try {
      await deleteProspect(recordId);
      toast.success("Contact deleted");
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error("Couldn't delete this contact — try again");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[500px] max-w-[94vw] bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isOwner ? op.address || bothNames : bothNames || record?.name}
              </h2>
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                {isOwner
                  ? `${isJoint ? "Owners" : "Owner"}, ${bothNames || "—"}`
                  : `${bp.buyer_type || "Buyer"} · ${record?.agent_name || ""}`}
                <TempChip temp={record?.lead_temp} />
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-2 flex-wrap mt-4">
            <button
              onClick={() =>
                primaryBlocked
                  ? toast.error(`${people[0]?.name || "This contact"} has asked not to be contacted.`)
                  : toast(`Calling ${record?.mobile} via AI Voice Caller…`)
              }
              className={`px-3 py-1.5 rounded-lg border text-xs font-bold flex items-center gap-1.5 ${
                primaryBlocked ? "border-rose-200 text-rose-400" : "border-gray-200"
              }`}
            >
              <Phone size={13} /> Call
            </button>
            <button
              onClick={() =>
                primaryBlocked
                  ? toast.error(`${people[0]?.name || "This contact"} has asked not to be contacted.`)
                  : toast(`Email ${record?.email || record?.mobile}`)
              }
              className={`px-3 py-1.5 rounded-lg border text-xs font-bold flex items-center gap-1.5 ${
                primaryBlocked ? "border-rose-200 text-rose-400" : "border-gray-200"
              }`}
            >
              <Mail size={13} /> Email
            </button>
            {!readOnly && (
            <button onClick={() => record && onEdit && onEdit(record)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold flex items-center gap-1.5">
              <Pencil size={13} /> Edit
            </button>
            )}
            {!readOnly && (
            <button
              onClick={() => openPrefs(0)}
              disabled={prefsFor !== null}
              title="Record what this person is looking to buy — suburbs, budget, property type. Feeds buyer matching."
              className="px-3 py-1.5 rounded-lg border border-[#55c5d0] text-[#0e7d86] bg-[#eaf7f8] text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            >
              {prefsFor === 0 ? <Loader2 size={13} className="animate-spin" /> : <SlidersHorizontal size={13} />}
              Buying preferences
            </button>
            )}
            {isOwner && (
              <button
                onClick={() => navigate(`/public-properties/${recordId}`)}
                title="Open this property's own profile - the Public Property page"
                className="px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 text-xs font-bold flex items-center gap-1.5"
              >
                <Layers size={13} /> Property profile
              </button>
            )}
            {isOwner && record?.status === "converted" && (
              <button
                onClick={record.converted_property
                  ? () => { navigate(`/properties/${record.converted_property}`); onClose(); }
                  : undefined}
                disabled={!record.converted_property}
                title="Already marked as an appraisal. Click to open it."
                className="px-3 py-1.5 rounded-lg bg-[#7c5cdb] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
              >
                <ArrowRightLeft size={13} /> Appraisal: open it
              </button>
            )}
            {!readOnly && isOwner && record?.status !== "converted" && (
              <button
                onClick={handleConvert}
                disabled={converting}
                title="Opens the Appraisal form with this property address filled in"
                className="px-3 py-1.5 rounded-lg bg-[#55c5d0] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              >
                {converting ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />}
                Convert to appraisal
              </button>
            )}
            {/* CRM-2026-00121: the way onto the Prospects board from here too.
                Not tied to readOnly: the page only passes onMoveToPipeline when
                the move is allowed, which includes the super admin on an
                agent's read-only Database. */}
            {isOwner && record?.status !== "converted" && onMoveToPipeline && (
              <button
                onClick={() => onMoveToPipeline(record)}
                title="Move to your Pipeline on Prospects: New, Cold, Warm, Hot, Long Term or Appraisal. The row stays in the database too."
                className="px-3 py-1.5 rounded-lg bg-[#0e7d86] text-white text-xs font-bold flex items-center gap-1.5"
              >
                <KanbanSquare size={13} /> Move to Pipeline
              </button>
            )}
            {readOnly ? (
              <span className="ml-auto px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold">
                View only
              </span>
            ) : (
              <button onClick={handleDelete} className="ml-auto px-3 py-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold flex items-center gap-1.5">
                <Trash2 size={13} /> Delete
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-5">
          {/* Do not contact, called out before anything else in the drawer: the
              agent opened this row to ring somebody. */}
          {unsubbedNames.length > 0 && (
            <div className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 mb-4">
              <Ban size={15} className="text-rose-600 mt-0.5 shrink-0" />
              <div className="text-[12.5px] text-rose-800">
                <b>Do not contact: {unsubbedNames.join(", ")}.</b>{" "}
                {unsubbedNames.length === people.length
                  ? "Excluded from every email and SMS."
                  : "The other contact on this row can still be reached."}
              </div>
            </div>
          )}
          {/* Details */}
          <Section title="Details">
            <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-sm">
              {isOwner ? (
                <>
                  <KV k="Suburb" v={op.suburb} />
                  <KV k="Type" v={`${op.property_type || "—"}${op.beds != null ? `, ${op.beds} bed ${op.baths ?? "?"} bath` : ""}`} />
                  <KV k="Land m²" v={op.land_sqm || "—"} />
                  <KV k="Last sold" v={soldSummary(op)} />
                  <KV k="List" v={record?.list_name || "—"} />
                </>
              ) : (
                <>
                  <KV k="Buyer type" v={bp.buyer_type} />
                  <KV k="Budget" v={money(bp.budget)} />
                  <KV k="Targets" v={(bp.target_suburbs || []).join(", ")} />
                  <KV k="Wants" v={`${bp.wanted_type || "—"}${bp.beds_min != null ? `, ${bp.beds_min}+ bed` : ""}`} />
                </>
              )}
            </dl>
          </Section>

          {/* Each person on the row is their own contact — one property, two owners. */}
          <Section title={isJoint ? `Contacts (${people.length})` : "Contact"}>
            {people.map((p, i) => (
              <PersonCard
                key={i}
                person={p}
                label={
                  isJoint
                    ? `${isOwner ? "Owner" : "Buyer"} ${i + 1}`
                    : isOwner
                    ? "Owner"
                    : "Buyer"
                }
                onPrefs={() => openPrefs(i)}
                prefsLoading={prefsFor === i}
                prefsDisabled={prefsFor !== null}
                onUnsubscribe={readOnly ? null : () => toggleUnsubscribed(i)}
                unsubLoading={unsubFor === i}
                onProfile={() => openProfile(i)}
                profileLoading={profileFor === i}
                profileDisabled={profileFor !== null}
              />
            ))}
            {isJoint && (
              <p className="text-[11px] text-gray-400 mt-1">
                Both owners are kept as separate contacts under this one property, so you can
                call, email or text either of them.
              </p>
            )}
          </Section>

          {/* Timeframe */}
          <Section title={isOwner ? "Looking to sell" : "Looking to buy"}>
            <div className="text-sm text-gray-800">{record?.timeframe || "Not stated"}</div>
            {isOwner && <MarketStatusLine op={op} />}
          </Section>

          {/* Next call */}
          {!readOnly && (
            <Section title="Set next call">
              <CallbackPresets onSave={handleNextCall} existing={record?.next_call_at} />
            </Section>
          )}

          {/* Agent Notes, with the AI summary of them up top (Monday 13202770775) */}
          <Section title="Agent Notes">
            <AgentNotesSummary
              notes={[...(record?.notes || []), ...linkedToSummaryNotes(linked)]}
              subject={isOwner ? op.address || record?.name : record?.name}
              className="mb-3"
            />
            {!readOnly && (
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="What did they say? This timestamps against the contact."
              className="w-full border border-gray-200 rounded-lg p-2.5 text-sm min-h-[58px] outline-none focus:border-[#55c5d0]"
            />
            )}
            {!readOnly && (
            <div className="flex gap-2 mt-2">
              <button onClick={handleNote} disabled={saving} className="px-3 py-1.5 rounded-lg bg-[#55c5d0] text-white text-xs font-bold disabled:opacity-50">
                Add note
              </button>
              <button onClick={handleReminder} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold flex items-center gap-1.5">
                <Clock size={13} /> Set reminder
              </button>
            </div>
            )}
            <div className="mt-3 flex flex-col gap-2">
              {(record?.notes || []).map((n) => (
                <div key={n.id} className="border-l-[3px] border-[#55c5d0] bg-gray-50 rounded-r-lg px-3 py-2">
                  <div className="text-[11px] text-gray-500 font-bold">
                    {new Date(n.created_at).toLocaleDateString()} {n.author_name ? `· ${n.author_name}` : ""}
                  </div>
                  <div className="text-[12.8px] text-gray-900 mt-0.5">{n.body}</div>
                </div>
              ))}
              {!(record?.notes || []).length && !linked.length && (
                <div className="text-xs text-gray-400">No notes yet.</div>
              )}
            </div>
            <LinkedNotesSection notes={linked} className="mt-3" />
          </Section>

          {/* Matching buyers (owners only) */}
          {isOwner && (
            <Section title={`Matching buyers, office-wide${matches.length ? ` (${matches.length})` : ""}`}>
              {matches.length ? (
                matches.map((m) => (
                  <div key={m.record_id} className="border border-gray-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center justify-between">
                      <b className="text-gray-900 text-sm">{m.name}</b>
                      <TempChip temp={m.lead_temp} />
                    </div>
                    <div className="text-xs text-gray-700 mt-1">
                      {money(m.budget)} budget · wants {(m.wanted_type || "any").toLowerCase()} in {(m.target_suburbs || []).join(", ")}
                    </div>
                    <div className="text-[11.5px] text-gray-500 mt-1.5">
                      {m.agent_name}
                      {m.is_other_agent ? " (other agent — coordinate to introduce)" : ""}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-gray-400">No buyers match yet. Every agent's buyers are searched here.</div>
              )}
            </Section>
          )}
        </div>
      </div>

      {/* Sits above the drawer (z-[60] vs z-50). Saves write through to the
          buyer's portal preferences, so they feed matching and buyer stats. */}
      {prefs && (
        <BuyerPreferencesModal
          identity={{ contactId: prefs.contactId }}
          title={prefs.title}
          onClose={() => setPrefs(null)}
        />
      )}
    </>
  );
}

/**
 * "Remind me to call again" series for a Database record. It is the same picker
 * agents already use on contact notes: a call-back time, a reason, then 1 day,
 * 1 week, 1 / 3 / 6 / 12 months, or a custom date (Christian Gravias, 2026-08-03).
 *
 * Each selection sets the record's callback date, adds the task, and mirrors a
 * reminder carrying the reason and time into Who-to-Call.
 */
const CallbackPresets = ({ onSave, existing }) => {
  const [saving, setSaving] = useState(null);   // preset key currently saving
  const [scheduled, setScheduled] = useState(null); // { label, date }
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [reason, setReason] = useState("follow_up");

  const save = async (payload, label, key) => {
    setSaving(key);
    try {
      const rec = await onSave({ ...payload, time, reason });
      const when = rec?.next_call_at || payload.date || "";
      setScheduled({ label, date: when });
      setShowCustom(false);
      // The grid reloads behind the drawer: this row turns green and drops to the
      // bottom in call-back order, and the dashboard reminder moves with it.
      toast.success(
        `Call-back set${when ? ` for ${when}` : ""}. Reminder updated, and this contact moves to the bottom of the list.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set the call-back. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  if (scheduled) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
          <Check size={14} />
          Call-back set for {scheduled.date || scheduled.label} at {time}
        </span>
        <button
          type="button"
          onClick={() => { setScheduled(null); setShowCustom(false); }}
          className="text-[11px] font-medium text-emerald-600 hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <BellPlus size={14} className="text-[#55c5d0]" />
          Remind me to call again
        </div>
        <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-600">
          <Clock size={14} className="text-[#55c5d0]" />
          at
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1 text-xs outline-none focus:border-[#55c5d0]"
          />
        </label>
      </div>

      <div className="mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
          <Tag size={14} className="text-[#55c5d0]" />
          Reason
        </div>
        <div className="flex flex-wrap gap-1.5">
          {REASONS.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setReason(r.key)}
              disabled={saving !== null}
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                reason === r.key
                  ? "border-[#55c5d0] bg-[#55c5d0] text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:border-[#55c5d0]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => save({ preset: p.key }, p.label, p.key)}
            disabled={saving !== null}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:border-[#55c5d0] disabled:opacity-50"
          >
            {saving === p.key && <Loader2 size={12} className="animate-spin" />}
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setShowCustom((s) => !s)}
          disabled={saving !== null}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
            showCustom
              ? "border-[#55c5d0] bg-[#e8f8fa] text-[#2b8f99]"
              : "border-gray-200 bg-white text-gray-700 hover:border-[#55c5d0]"
          }`}
        >
          <CalendarClock size={12} />
          Custom
        </button>
      </div>

      {showCustom && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
            className="rounded-lg border border-gray-200 px-2 py-1 text-xs outline-none focus:border-[#55c5d0]"
          />
          <button
            type="button"
            onClick={() => customDate && save({ date: customDate }, customDate, "custom")}
            disabled={!customDate || saving !== null}
            className="inline-flex items-center gap-1 rounded-lg bg-[#55c5d0] px-3 py-1 text-xs font-bold text-white disabled:opacity-50"
          >
            {saving === "custom" ? <Loader2 size={12} className="animate-spin" /> : "Set"}
          </button>
        </div>
      )}

      {existing && (
        <div className="mt-2 text-[11px] font-extrabold text-[#34b56a] flex items-center gap-1.5">
          <Clock size={12} /> Call-back currently set for {existing}. In your Tasks.
        </div>
      )}
    </div>
  );
};

/**
 * One person on the row with their own Call / Email actions. A jointly-owned
 * property lists two of these — the client's "split into 2 contacts under the
 * same property profile" (Christian Gravias, 2026-08-03).
 */
const PersonCard = ({
  person, label, onPrefs, prefsLoading, prefsDisabled, onUnsubscribe, unsubLoading,
  onProfile, profileLoading, profileDisabled,
}) => {
  const mobile = person.mobiles[0];
  const email = person.emails[0];
  const blocked = Boolean(person.unsubscribed);
  // Call / Email stay clickable when blocked and refuse with a reason. A disabled
  // button just looks broken; this one says why.
  const refuse = () =>
    toast.error(`${person.name || "This contact"} has asked not to be contacted.`);
  return (
    <div className={`border rounded-lg p-3 mb-2 flex items-start justify-between gap-3 ${
      blocked ? "border-rose-200 bg-rose-50/60" : "border-gray-200"
    }`}>
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-extrabold">{label}</div>
        <b className={`text-sm ${blocked ? "text-gray-500 line-through" : "text-gray-900"}`}>
          {person.name || "Name not given"}
        </b>
        {blocked && (
          <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-extrabold uppercase tracking-wide align-middle">
            <Ban size={10} /> Do not contact
          </span>
        )}
        <div className="text-[12.5px] text-gray-700 mt-0.5">
          {person.mobiles.join(", ") || <span className="text-gray-400">No mobile</span>}
        </div>
        <div className="text-[12.5px] text-gray-700 break-all">
          {person.emails.join(", ") || <span className="text-gray-400">No email</span>}
        </div>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <button
          onClick={() =>
            blocked ? refuse()
              : mobile ? toast(`Calling ${mobile} via AI Voice Caller…`)
              : toast.error("No mobile for this contact")
          }
          className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-40 ${
            blocked ? "border-rose-200 text-rose-400" : "border-gray-200"
          }`}
          disabled={!mobile && !blocked}
        >
          <Phone size={12} /> Call
        </button>
        <button
          onClick={() =>
            blocked ? refuse()
              : email ? toast(`Email ${email}`)
              : toast.error("No email for this contact")
          }
          className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-40 ${
            blocked ? "border-rose-200 text-rose-400" : "border-gray-200"
          }`}
          disabled={!email && !blocked}
        >
          <Mail size={12} /> Email
        </button>
        {/* The unsubscribe button itself, per person: one owner of a jointly-owned
            property can ask to be left alone without silencing the other. */}
        {onUnsubscribe && (
          <button
            onClick={onUnsubscribe}
            disabled={unsubLoading}
            title={
              blocked
                ? "On the Do not contact list. Click to allow contact again."
                : "Stop every email and SMS to this person"
            }
            className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-40 ${
              blocked
                ? "border-gray-200 text-gray-600"
                : "border-rose-200 text-rose-600 bg-rose-50"
            }`}
          >
            {unsubLoading ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
            {blocked ? "Allow" : "Unsubscribe"}
          </button>
        )}
        {/* Each person keeps their OWN buying preferences — two owners of one
            property can be after very different things. No mobile or email
            needed: the row's contact is what the editor keys on. */}
        <button
          onClick={onPrefs}
          disabled={prefsDisabled}
          title="What is this person looking to buy?"
          className="px-2.5 py-1 rounded-lg border border-[#55c5d0] text-[#0e7d86] bg-[#eaf7f8] text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-40"
        >
          {prefsLoading ? <Loader2 size={12} className="animate-spin" /> : <SlidersHorizontal size={12} />}
          Prefs
        </button>
        {/* Every person on a row is a CRM contact with a full profile behind them
            — notes, reminders, comms history and every OTHER property they own.
            Without this the Database was a dead end (CRM-2026-00031). */}
        <button
          onClick={onProfile}
          disabled={profileDisabled}
          title="Open this person's contact profile"
          className="px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-40"
        >
          {profileLoading ? <Loader2 size={12} className="animate-spin" /> : <User size={12} />}
          Profile
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mt-5 first:mt-0">
    <h4 className="text-[11.5px] uppercase tracking-wide text-gray-400 font-extrabold mb-2.5">{title}</h4>
    {children}
  </div>
);

const KV = ({ k, v }) => (
  <>
    <dt className="text-gray-400 font-bold">{k}</dt>
    <dd className="text-gray-900 font-semibold">{v || "—"}</dd>
  </>
);
